/**
 * Keluar Sementara & Kembali — Tugas Luar (2026-10-12, Modul Kehadiran Tahap 4).
 *
 * Alur: Ajukan → disetujui pemeriksa → Mulai Keluar (waktu server + geo) →
 * Sudah Kembali (geo) ATAU Selesai Tugas & Absen Pulang dari Luar (geo+foto+laporan).
 * - Terpisah dari Absen Pulang; satu sesi aktif; multi sesi per hari tanpa
 *   tumpang tindih estimasi.
 * - Keluar mendadak: langsung open + menunggu persetujuan + notifikasi atasan.
 * - Perpanjangan estimasi: riwayat estimasi awal disimpan; pemeriksa memutuskan.
 * - Status keberadaan: Di Lokasi / Tugas Luar / Keluar Pribadi / Sudah Pulang.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  LogOut, LogIn, CheckCircle2, XCircle, AlertTriangle, Loader2, MapPin,
  FilePlus2, Ban, Clock, Camera, Timer, ShieldAlert, Flag,
} from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SearchableSelect from "@/components/SearchableSelect";

const KINDS = [
  { value: "office_duty", label: "Tugas Kantor" },
  { value: "personal", label: "Keperluan Pribadi" },
];
const PLANS = [
  { value: "return", label: "Rencana kembali ke lokasi" },
  { value: "finish_outside", label: "Selesai di luar (tidak kembali)" },
];
const PRESENCE = {
  in: { label: "Di Lokasi", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  tugas_luar: { label: "Tugas Luar", cls: "text-sky-700 bg-sky-50 border-sky-200" },
  keluar_pribadi: { label: "Keluar Pribadi", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  out: { label: "Sudah Pulang", cls: "text-slate-600 bg-slate-100 border-slate-300" },
  unknown: { label: "Belum Absen", cls: "text-slate-500 bg-slate-50 border-slate-200" },
};
const APPROVAL_META = {
  pending_approval: { label: "Menunggu Persetujuan", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  approved: { label: "Disetujui", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  rejected: { label: "Ditolak", cls: "text-rose-700 bg-rose-50 border-rose-200" },
};
const SESSION_META = {
  not_started: "Belum Mulai",
  open: "Sedang Berjalan",
  closed: "Sudah Kembali",
  closed_outside: "Selesai di Luar",
  cancelled: "Dibatalkan",
};

function fmtT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtD(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("id-ID", { weekday: "short", day: "numeric", month: "short" }); }
  catch { return d; }
}
function fmtDur(min) {
  if (min == null) return "—";
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return h ? `${h} jam ${m} mnt` : `${m} mnt`;
}

function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Browser tidak mendukung geolokasi."));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy }),
      (e) => reject(new Error(`Gagal mengambil lokasi: ${e.message}`)),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

function BreakForm({ defaultDate, onSaved, onCancel }) {
  const [f, setF] = useState({
    work_date: defaultDate, kind: "office_duty", plan: "return", reason: "",
    destination: "", est_departure: "", est_return: "", related_task: "", immediate: false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries({ work_date: f.work_date, kind: f.kind, plan: f.plan, reason: f.reason, destination: f.destination, est_departure: f.est_departure })
        .forEach(([k, v]) => fd.append(k, v));
      if (f.plan === "return" && f.est_return) fd.append("est_return", f.est_return);
      if (f.related_task) fd.append("related_task", f.related_task);
      if (f.immediate) {
        fd.append("immediate", "true");
        const g = await getGeo();
        fd.append("latitude", String(g.lat)); fd.append("longitude", String(g.lon)); fd.append("accuracy", String(g.acc));
      }
      await api.post("/attendance/breaks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(f.immediate ? "Keluar mendadak tercatat — menunggu persetujuan pemeriksa." : "Pengajuan terkirim ke pemeriksa.");
      onSaved();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal mengirim pengajuan."); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[#0B1D3A] bg-white p-4 space-y-2" data-testid="break-form">
      <div className="font-display text-sm text-[#0B1D3A]">Ajukan Keluar Sementara</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <SearchableSelect value={f.kind} onChange={(v) => setF({ ...f, kind: v })} options={KINDS} placeholder="Jenis" testid="break-kind" />
        <SearchableSelect value={f.plan} onChange={(v) => setF({ ...f, plan: v })} options={PLANS} placeholder="Rencana" testid="break-plan" />
        <Input type="date" value={f.work_date} min={defaultDate} onChange={(e) => setF({ ...f, work_date: e.target.value })} data-testid="break-date" />
        <Input placeholder="Tujuan *" value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} data-testid="break-destination" />
        <Input type="time" value={f.est_departure} onChange={(e) => setF({ ...f, est_departure: e.target.value })} data-testid="break-est-dep" title="Estimasi berangkat" />
        {f.plan === "return" && (
          <Input type="time" value={f.est_return} onChange={(e) => setF({ ...f, est_return: e.target.value })} data-testid="break-est-ret" title="Estimasi kembali" />
        )}
        <Textarea placeholder={f.kind === "personal" ? "Alasan/kegiatan * (cukup secara administratif — tanpa rincian sensitif)" : "Alasan/kegiatan *"}
          value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} rows={2} className="md:col-span-3 rounded-none" data-testid="break-reason" />
        <Input placeholder="Proyek/tugas terkait (opsional, informasional — tidak mengotorisasi otomatis)" value={f.related_task}
          onChange={(e) => setF({ ...f, related_task: e.target.value })} className="md:col-span-2" data-testid="break-related" />
        <label className="inline-flex items-center gap-2 text-[11px]" data-testid="break-immediate-label">
          <input type="checkbox" checked={f.immediate} onChange={(e) => setF({ ...f, immediate: e.target.checked })} data-testid="break-immediate" />
          Keluar mendadak (mulai sekarang, lokasi dibaca saat kirim)
        </label>
      </div>
      <div className="text-[10px] text-slate-500 italic">
        Waktu berangkat/kembali dicatat dari jam server saat Anda menekan Mulai Keluar / Sudah Kembali — bukan waktu pengajuan.
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={busy || !f.reason.trim() || !f.destination.trim() || !f.est_departure || (f.plan === "return" && !f.est_return)}
          className="rounded-none bg-[#0B1D3A]" data-testid="break-submit">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <FilePlus2 size={12} />} Kirim
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="rounded-none" data-testid="break-cancel-form">Batal</Button>
      </div>
    </div>
  );
}

function ActionGeoPhoto({ label, testid, onDone, needPhoto, extra }) {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState("");
  const run = async () => {
    setBusy(true);
    try {
      const g = await getGeo();
      const fd = new FormData();
      fd.append("latitude", String(g.lat)); fd.append("longitude", String(g.lon)); fd.append("accuracy", String(g.acc));
      if (file) fd.append("photo", file);
      if (extra === "report") fd.append("report", report);
      await onDone(fd);
    } catch (e) { toast.error(e.message || formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="inline-flex items-center gap-1 text-[10px] text-slate-600 border border-dashed border-slate-300 px-2 py-1 cursor-pointer" data-testid={`${testid}-photo`}>
        <Camera size={10} /> {file ? file.name : (needPhoto ? "Foto *" : "Foto (opsional)")}
        <input type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      {extra === "report" && (
        <Input placeholder="Laporan singkat *" value={report} onChange={(e) => setReport(e.target.value)} className="h-7 text-[11px] w-56" data-testid={`${testid}-report`} />
      )}
      <Button size="sm" onClick={run} disabled={busy || (needPhoto && !file) || (extra === "report" && !report.trim())}
        className="rounded-none h-7 text-[10px] bg-[#0B1D3A]" data-testid={testid}>
        {busy ? <Loader2 size={10} className="animate-spin" /> : <MapPin size={10} />} {label}
      </Button>
    </div>
  );
}

function BreakCard({ brk, meId, isReview, today, requirePhotoBreak, onChanged }) {
  const [extOpen, setExtOpen] = useState(false);
  const [extVal, setExtVal] = useState("");
  const [extReason, setExtReason] = useState("");
  const [decReason, setDecReason] = useState("");
  const [busy, setBusy] = useState(false);
  const mine = brk.user_id === meId;
  const actionable = brk.work_date === today;

  const post = async (url, body, okMsg) => {
    setBusy(true);
    try {
      await api.post(url, body);
      toast.success(okMsg);
      onChanged();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Aksi gagal."); }
    finally { setBusy(false); }
  };
  const decide = (action) => {
    if (action === "reject" && !decReason.trim()) { toast.error("Alasan penolakan wajib diisi."); return; }
    post(`/attendance/breaks/${brk.id}/decide`, { action, reason: decReason.trim() || null },
      action === "approve" ? "Pengajuan disetujui." : "Pengajuan ditolak.");
  };
  const decideExt = (extId, action) => {
    const fd = new FormData();
    fd.append("extension_id", extId); fd.append("action", action);
    if (decReason.trim()) fd.append("reason", decReason.trim());
    post(`/attendance/breaks/${brk.id}/extend-decide`, fd, "Keputusan perpanjangan tersimpan.");
  };
  const extend = () => {
    const fd = new FormData();
    fd.append("new_est_return", extVal); fd.append("reason", extReason);
    post(`/attendance/breaks/${brk.id}/extend`, fd, "Pengajuan perpanjangan terkirim.");
    setExtOpen(false);
  };
  const cancel = () => {
    if (!window.confirm("Batalkan sesi ini?")) return;
    post(`/attendance/breaks/${brk.id}/cancel`, null, "Sesi dibatalkan.");
  };

  const am = APPROVAL_META[brk.approval_status] || {};
  return (
    <div className="border border-slate-200 bg-white p-3 space-y-2" data-testid={`break-card-${brk.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-[#0B1D3A]">
            {brk.kind_label} <span className="text-slate-400 font-normal">· {fmtD(brk.work_date)}</span>
            {brk.sudden && <span className="ml-1 text-[9px] uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-1 py-0.5">Mendadak</span>}
            {brk.auto_approved && <span className="ml-1 text-[9px] uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-200 px-1 py-0.5">Otorisasi Penugasan</span>}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {brk.destination} · estimasi {brk.est_departure}–{brk.est_return || "akhir hari"}
            {brk.related_task && <span> · tugas: {brk.related_task}</span>}
          </div>
          <div className="text-[10px] text-slate-500">Pemeriksa: {brk.reviewer_name || "—"}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 ${am.cls}`} data-testid={`break-approval-${brk.id}`}>{am.label}</span>
          <span className="text-[9px] uppercase tracking-wider text-slate-500" data-testid={`break-session-${brk.id}`}>{SESSION_META[brk.session_status]}</span>
          {brk.evidence_status === "needs_review" && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">
              <AlertTriangle size={9} /> Perlu Verifikasi
            </span>
          )}
        </div>
      </div>
      <div className="text-[11px] text-slate-700">{brk.reason}</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500">
        {brk.actual_start_at && <span>Berangkat: <span className="font-mono">{fmtT(brk.actual_start_at)}</span></span>}
        {brk.actual_return_at && <span>Kembali/Selesai: <span className="font-mono">{fmtT(brk.actual_return_at)}</span></span>}
        {brk.duration_minutes != null && <span>Durasi: {fmtDur(brk.duration_minutes)}</span>}
        {brk.needs_review_reason && <span className="text-amber-700">{brk.needs_review_reason}</span>}
        {brk.finish_report && <span className="text-slate-600">Laporan: {brk.finish_report}</span>}
        {brk.decision_reason && <span className="text-slate-600">Keputusan: {brk.decision_reason}</span>}
      </div>
      {(brk.est_history || []).length > 1 && (
        <div className="text-[10px] text-slate-500">
          Riwayat estimasi: {brk.est_history.map((h, i) => `${h.est_departure}–${h.est_return || "akhir hari"}${i === 0 ? " (awal)" : ""}`).join(" → ")}
        </div>
      )}
      {(brk.extensions || []).map((e) => (
        <div key={e.id} className="flex items-center justify-between gap-2 text-[10px] border border-slate-100 bg-slate-50 px-2 py-1" data-testid={`break-ext-${e.id}`}>
          <span>Perpanjangan → {e.new_est_return} ({e.reason}) — {e.status === "pending" ? "menunggu" : e.status === "approved" ? "disetujui" : "ditolak"}</span>
          {isReview && e.status === "pending" && (
            <span className="flex gap-1">
              <button type="button" onClick={() => decideExt(e.id, "approve")} className="text-emerald-700 font-semibold" data-testid={`break-ext-approve-${e.id}`}>Setujui</button>
              <button type="button" onClick={() => decideExt(e.id, "reject")} className="text-rose-600 font-semibold" data-testid={`break-ext-reject-${e.id}`}>Tolak</button>
            </span>
          )}
        </div>
      ))}

      {/* Aksi pemilik */}
      {mine && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
          {brk.session_status === "not_started" && brk.approval_status === "approved" && actionable && (
            <ActionGeoPhoto label="Mulai Keluar" testid={`break-start-${brk.id}`} needPhoto={requirePhotoBreak}
              onDone={(fd) => api.post(`/attendance/breaks/${brk.id}/start`, fd, { headers: { "Content-Type": "multipart/form-data" } })
                .then(() => { toast.success("Sesi dimulai — waktu server tercatat."); onChanged(); })} />
          )}
          {brk.session_status === "open" && brk.plan === "return" && actionable && (
            <>
              <ActionGeoPhoto label="Sudah Kembali" testid={`break-return-${brk.id}`} needPhoto={requirePhotoBreak}
                onDone={(fd) => api.post(`/attendance/breaks/${brk.id}/return`, fd, { headers: { "Content-Type": "multipart/form-data" } })
                  .then(() => { toast.success("Sesi ditutup — selamat kembali."); onChanged(); })} />
              <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => setExtOpen(!extOpen)} data-testid={`break-extend-${brk.id}`}>
                <Timer size={10} /> Perpanjang
              </Button>
            </>
          )}
          {brk.session_status === "open" && brk.plan === "finish_outside" && brk.kind === "office_duty" && actionable && (
            <ActionGeoPhoto label="Selesai Tugas & Absen Pulang dari Luar" testid={`break-finish-${brk.id}`} needPhoto extra="report"
              onDone={(fd) => api.post(`/attendance/breaks/${brk.id}/finish-outside`, fd, { headers: { "Content-Type": "multipart/form-data" } })
                .then(() => { toast.success("Sesi ditutup & absen pulang tercatat."); onChanged(); })} />
          )}
          {brk.session_status === "not_started" && (
            <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] text-rose-600" onClick={cancel} data-testid={`break-cancel-${brk.id}`}>
              <Ban size={10} /> Batalkan
            </Button>
          )}
          {brk.session_status === "open" && !actionable && (
            <span className="text-[10px] text-amber-700 inline-flex items-center gap-1"><AlertTriangle size={10} /> Sesi belum selesai — ajukan koreksi di tab Pengajuan &amp; Persetujuan (tidak ditutup otomatis).</span>
          )}
        </div>
      )}
      {extOpen && (
        <div className="border border-sky-200 bg-sky-50 p-2 flex flex-wrap items-center gap-2" data-testid={`break-extend-form-${brk.id}`}>
          <Input type="time" value={extVal} onChange={(e) => setExtVal(e.target.value)} className="h-7 w-28 text-[11px]" data-testid={`break-ext-time-${brk.id}`} />
          <Input placeholder="Alasan perpanjangan *" value={extReason} onChange={(e) => setExtReason(e.target.value)} className="h-7 text-[11px] flex-1 min-w-[180px]" data-testid={`break-ext-reason-${brk.id}`} />
          <Button size="sm" onClick={extend} disabled={busy || !extVal || !extReason.trim()} className="rounded-none h-7 text-[10px] bg-[#0B1D3A]" data-testid={`break-ext-submit-${brk.id}`}>Kirim</Button>
        </div>
      )}
      {/* Aksi pemeriksa */}
      {isReview && brk.approval_status === "pending_approval" && (
        <div className="border-t border-slate-100 pt-2 space-y-2" data-testid={`break-decide-${brk.id}`}>
          <Textarea placeholder="Alasan keputusan (wajib untuk tolak)" value={decReason} onChange={(e) => setDecReason(e.target.value)} rows={1} className="rounded-none text-xs" data-testid={`break-decide-reason-${brk.id}`} />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => decide("approve")} disabled={busy} className="rounded-none h-7 text-[10px] bg-emerald-700 hover:bg-emerald-800" data-testid={`break-approve-${brk.id}`}><CheckCircle2 size={11} /> Setujui</Button>
            <Button size="sm" onClick={() => decide("reject")} disabled={busy} className="rounded-none h-7 text-[10px] bg-rose-600 hover:bg-rose-700" data-testid={`break-reject-${brk.id}`}><XCircle size={11} /> Tolak</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function KeluarTugasLuarPanel() {
  const [view, setView] = useState("mine");
  const [mine, setMine] = useState([]);
  const [review, setReview] = useState([]);
  const [summary, setSummary] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [noReviewer, setNoReviewer] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [m, rv, sm, td, meR, asgR, stR] = await Promise.all([
        api.get("/attendance/breaks?role=mine"),
        api.get("/attendance/breaks?role=review").catch(() => ({ data: [] })),
        api.get("/attendance/breaks/summary"),
        api.get("/attendance/my-today"),
        api.get("/auth/me"),
        api.get("/attendance/assignments?active_only=true").catch(() => ({ data: [] })),
        api.get("/attendance/settings").catch(() => ({ data: {} })),
      ]);
      setMine(m.data || []); setReview(rv.data || []); setSummary(sm.data);
      setTodayData(td.data); setMe(meR.data);
      const anyReviewer = (asgR.data || []).some((a) => a.reviewer_user_id);
      setNoReviewer(!anyReviewer && !(stR.data || {}).default_reviewer_user_id);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal memuat sesi."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Ringan: refresh berkala utk badge overrun (bukan pelacakan real-time).
  useEffect(() => {
    pollRef.current = setInterval(load, 60_000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const today = todayData?.today;
  const presence = PRESENCE[todayData?.latest_presence || "unknown"];
  const requirePhotoBreak = !!todayData?.assignment?.require_photo_break;
  const pendingReview = useMemo(
    () => review.filter((b) => (b.approval_status === "pending_approval" && b.user_id !== me?.id)
      || (b.extensions || []).some((e) => e.status === "pending")),
    [review, me]);

  // Timeline harian: gabungkan absen + sesi, urut waktu.
  const timeline = useMemo(() => {
    const ev = todayData?.event;
    const items = [];
    if (ev?.clock_in_at) items.push({ t: ev.clock_in_at, label: "Absen Masuk", icon: "in" });
    for (const b of todayData?.today_breaks || []) {
      if (b.actual_start_at) items.push({ t: b.actual_start_at, label: `Mulai Keluar — ${b.kind_label} (${b.destination})`, icon: "out" });
      if (b.actual_return_at) items.push({ t: b.actual_return_at, label: b.session_status === "closed_outside" ? "Selesai Tugas di Luar" : "Kembali", icon: "back" });
    }
    if (ev?.clock_out_at) items.push({ t: ev.clock_out_at, label: "Absen Pulang", icon: "done" });
    return items.sort((a, b) => a.t.localeCompare(b.t));
  }, [todayData]);

  if (loading) return <div className="p-6 text-center text-slate-500 text-xs"><Loader2 className="inline animate-spin" size={14} /> Memuat…</div>;

  return (
    <div className="space-y-3" data-testid="att-break-panel">
      {noReviewer && (
        <div className="border border-rose-200 bg-rose-50 px-3 py-2 flex items-start gap-2" data-testid="break-no-reviewer">
          <ShieldAlert size={14} className="text-rose-700 mt-0.5 shrink-0" />
          <div className="text-[11px] text-rose-800"><span className="font-semibold uppercase tracking-wider">Pemeriksa belum dikonfigurasi. </span>
            Pengajuan tidak dapat dikirim sampai HR mengisi pemeriksa pada penugasan atau pemeriksa default.</div>
        </div>
      )}
      {/* Status keberadaan + ringkasan durasi */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className={`border px-3 py-2 ${presence.cls}`} data-testid="presence-badge">
          <div className="text-[9px] uppercase tracking-wider opacity-70">Status Keberadaan</div>
          <div className="text-sm font-semibold">{presence.label}</div>
        </div>
        <div className="border border-slate-200 bg-white px-3 py-2" data-testid="break-sum-office">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Tugas Kantor Hari Ini</div>
          <div className="text-sm font-semibold tabular-nums">{fmtDur(summary?.office_duty_minutes)}</div>
        </div>
        <div className="border border-slate-200 bg-white px-3 py-2" data-testid="break-sum-personal">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Pribadi Hari Ini</div>
          <div className="text-sm font-semibold tabular-nums">{fmtDur(summary?.personal_minutes)}</div>
          {summary?.personal_counts_as_leave && <div className="text-[9px] text-slate-400">diakui sbg izin (tanpa potong gaji otomatis)</div>}
        </div>
        <div className="border border-slate-200 bg-white px-3 py-2" data-testid="break-sum-union">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Total di Luar Lokasi</div>
          <div className="text-sm font-semibold tabular-nums">{fmtDur(summary?.out_of_office_union_minutes)}</div>
          {summary?.live_open_minutes > 0 && <div className="text-[9px] text-sky-700">berjalan: {fmtDur(summary.live_open_minutes)}</div>}
        </div>
      </div>
      {/* Timeline harian */}
      <div className="border border-slate-200 bg-white p-3" data-testid="break-timeline">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Clock size={11} /> Timeline Hari Ini</div>
        {timeline.length === 0 && <div className="text-[11px] text-slate-400 italic">Belum ada kejadian hari ini.</div>}
        <div className="space-y-1">
          {timeline.map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px]">
              <span className="font-mono text-slate-500 w-12">{fmtT(t.t)}</span>
              {t.icon === "in" && <LogIn size={11} className="text-emerald-600" />}
              {t.icon === "out" && <LogOut size={11} className="text-sky-600" />}
              {t.icon === "back" && <Flag size={11} className="text-emerald-600" />}
              {t.icon === "done" && <CheckCircle2 size={11} className="text-slate-500" />}
              <span>{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 border-b border-slate-200">
          <button type="button" onClick={() => setView("mine")} data-testid="break-view-mine"
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 ${view === "mine" ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500"}`}>
            Sesi Saya ({mine.length})
          </button>
          <button type="button" onClick={() => setView("review")} data-testid="break-view-review"
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border-b-2 ${view === "review" ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500"}`}>
            Perlu Persetujuan ({pendingReview.length})
          </button>
        </div>
        {view === "mine" && (
          <Button size="sm" className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]" onClick={() => setShowForm(!showForm)} data-testid="break-new-btn">
            <FilePlus2 size={14} /> Ajukan Keluar
          </Button>
        )}
      </div>
      {showForm && view === "mine" && <BreakForm defaultDate={today} onSaved={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />}
      <div className="space-y-2">
        {(view === "mine" ? mine : review).length === 0 && (
          <div className="border border-slate-200 px-3 py-6 text-center text-slate-400 italic text-xs">
            {view === "mine" ? "Belum ada sesi keluar sementara." : "Tidak ada pengajuan yang perlu Anda periksa."}
          </div>
        )}
        {(view === "mine" ? mine : review).map((b) => (
          <BreakCard key={b.id} brk={b} meId={me?.id} isReview={view === "review" && b.user_id !== me?.id}
            today={today} requirePhotoBreak={requirePhotoBreak} onChanged={load} />
        ))}
      </div>
      <div className="text-[10px] text-slate-500 italic">
        Sesi yang lupa ditutup tidak ditutup otomatis — ajukan koreksi di tab Pengajuan &amp; Persetujuan.
        Pengingat overrun dikirim via notifikasi in-app (tersimpan saat aplikasi ditutup, muncul saat dibuka kembali).
      </div>
    </div>
  );
}
